import React, { useEffect, useRef, useState } from "react";
import { FocusScope, usePreventScroll } from "react-aria";
import { t } from "ttag";

import { ServerCmsContextProcessorsNavigationProcessor } from "@reactivated";

import { classNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { AnnouncementBar } from "./AnnouncementBar";
import { MainMenu } from "./MainMenu";

import styles from "./index.module.scss";

export type Overlap = "light" | "dark" | null;
interface Props {
    header_nav: ServerCmsContextProcessorsNavigationProcessor["header_nav"];
    overlapNav: Overlap;
    sticky?: boolean;
}

export const HeaderNavigation = ({
    header_nav,
    overlapNav,
    sticky = true,
}: Props) => {
    const announcementRef = useRef<HTMLDivElement | null>(null);
    /**
     * On a basic level this exists so the burger button can trigger/hide the
     * main menu in the mobileLayout, but we're also using it to trigger all
     * of the style changes that come along with it.
     */
    const [menuActive, setMenuActive] = useState(false);
    const [scrolledABit, setScrolledABit] = useState(false);

    const keyHandler = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            if (menuActive) setMenuActive(false);
        }
    };

    const getNavRootClass = () => {
        if (menuActive) return styles.darkNoBackground;
        if (scrolledABit) return styles.darkBackground;
        if (overlapNav) return styles[`${overlapNav}OverlapNav`];
        return styles.darkBackground;
    };

    useEffect(() => {
        if (
            typeof window !== "undefined" &&
            announcementRef.current &&
            menuActive
        ) {
            const announcementHeight =
                announcementRef.current.getBoundingClientRect().height;

            if (window.scrollY < announcementHeight) {
                window.scrollTo({
                    top: announcementHeight,
                    behavior: "smooth",
                });
            }
        }

        if (typeof document !== "undefined") {
            document.addEventListener("keydown", keyHandler);
        }

        return () => {
            if (typeof document !== "undefined") {
                document.removeEventListener("keydown", keyHandler);
            }
        };
    }, [menuActive]);

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined")
            return;

        const handleScroll = () => setScrolledABit(window.scrollY > 100);

        window.addEventListener("scroll", handleScroll);

        return () => {
            if (typeof window === "undefined") return;
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    usePreventScroll({ isDisabled: !menuActive });

    const label = t`Main Site Navigation`;

    if (!header_nav) return <></>;

    const { announcement_bar, menu } = header_nav;

    return (
        <>
            {announcement_bar.value.length > 0 && (
                <AnnouncementBar
                    announcements={announcement_bar.value}
                    menuActive={menuActive}
                    ref={announcementRef}
                />
            )}
            <FocusScope contain={menuActive}>
                <nav
                    className={classNames([
                        [styles.nav, true],
                        [styles.sticky, sticky],
                    ])}
                >
                    <div aria-label={label} className={getNavRootClass()}>
                        <div className={styles.grid}>
                            <div className={styles.inner}>
                                <div className={styles.topRow}>
                                    <Clickable href="/" className={styles.logo}>
                                        <Svg
                                            name="logo-hyundai"
                                            visuallyHiddenText={t`Hyundai XCIENT`}
                                        />
                                    </Clickable>

                                    <Clickable
                                        onPress={() =>
                                            setMenuActive(!menuActive)
                                        }
                                        className={styles.burgerTrigger}
                                    >
                                        <Svg
                                            name="burger"
                                            className={
                                                menuActive
                                                    ? styles.xIcon
                                                    : styles.burger
                                            }
                                            visuallyHiddenText={`${
                                                menuActive
                                                    ? t`Close Header Navigation`
                                                    : t`Open Header Navigation`
                                            }`}
                                        />
                                    </Clickable>
                                </div>
                            </div>
                        </div>
                    </div>
                    <MainMenu
                        menu={menu}
                        active={menuActive}
                        closeMenu={() => setMenuActive(false)}
                    />
                </nav>
            </FocusScope>
        </>
    );
};
